// /reports — tenant workspace: the reports index. The current
// reporting scope plus six perspectives on the operation, each with its
// headline metric. The period selector re-queries the window; bot + channel are
// single-valued today (one bot, WhatsApp only) so they're shown statically.
// "Exportar todos" downloads the headlines as CSV. Data via getReports().

import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  getReports,
  type ReportId,
  type ReportPeriod,
  REPORT_PERIODS,
  type ReportsData,
  type ReportSummary,
} from './reports-data.js'
import { Button, Chip, DropdownMenu, PageHeader, Spinner } from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'

const REPORT_WRAP: Record<ReportId, string> = {
  conversaciones: 'bg-info-bg text-info',
  prospectos: 'bg-[color-mix(in_srgb,var(--brand-blue)_12%,transparent)] text-link',
  equipo: 'bg-ok-bg text-ok',
  tiempos: 'bg-info-bg text-info',
  conocimiento: 'bg-warn-bg text-warn',
  citas: 'bg-ok-bg text-ok',
}

const REPORT_PATHS: Record<ReportId, ReactNode> = {
  conversaciones: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />,
  prospectos: <path d="M3 6h18M3 12h18M3 18h12" />,
  equipo: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M2.5 20c0-3.4 3-5.2 6.5-5.2s6.5 1.8 6.5 5.2" />
      <path d="M16 5.2A3.4 3.4 0 0 1 16 12M17.5 14.9c2.8.4 4.5 2.1 4.5 5.1" />
    </>
  ),
  tiempos: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  conocimiento: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a3 3 0 1 1 4.5 2.5c-1 .5-1.5 1-1.5 2M12 17h.01" />
    </>
  ),
  citas: (
    <>
      <rect x="3" y="4.5" width="18" height="17" rx="2.4" />
      <path d="M3 9.5h18M8 2.5v4M16 2.5v4M9 14l2 2 4-4" />
    </>
  ),
}

const DELTA_TONE = {
  up: 'text-ok',
  warn: 'text-warn',
  neutral: 'text-text-2',
} as const

function Icon({ children }: { children: ReactNode }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  )
}

/** The inner content of a scope chip (icon · value · hint), shared by the
 *  interactive (period dropdown) and static (bot/channel) variants. */
function ScopeInner({
  children,
  value,
  hint,
  chevron,
}: {
  children: ReactNode
  value: string
  hint: string
  chevron?: boolean
}): JSX.Element {
  return (
    <>
      <span className="flex size-6 flex-none items-center justify-center rounded-md bg-surface text-text-2 [&_svg]:size-[13px]">
        {children}
      </span>
      <span className="text-[12px] font-semibold text-text">{value}</span>
      <span className="text-[10.5px] text-text-3">{hint}</span>
      {chevron && (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="size-[13px] text-text-3"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      )}
    </>
  )
}

function ReportCard({ r, onOpen }: { r: ReportSummary; onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col rounded-lg border border-line bg-surface p-[18px] text-left transition-[box-shadow,transform] duration-normal ease-vysto hover:-translate-y-[2px] hover:shadow-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={`grid size-10 flex-none place-items-center rounded-xl [&_svg]:size-[20px] ${REPORT_WRAP[r.id]}`}
        >
          <Icon>{REPORT_PATHS[r.id]}</Icon>
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[13.5px] font-bold text-text">{r.title}</h3>
            {r.verticalBadge && <Chip tone="info">{r.verticalBadge}</Chip>}
          </div>
          <p className="mt-0.5 text-[11.5px] leading-snug text-text-3">{r.description}</p>
        </div>
      </div>

      <div className="mt-4 w-full border-t border-line pt-3.5">
        <div className="text-[26px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-text">
          {r.value}
          {r.unit && <span className="ml-1 text-[15px] font-semibold text-text-2">{r.unit}</span>}
        </div>
        <div className="mt-1.5 text-[11.5px] text-text-2">{r.statLabel}</div>
        <div className={`mt-0.5 text-[11.5px] font-semibold ${DELTA_TONE[r.delta.tone]}`}>
          {r.delta.text}
        </div>
      </div>
    </button>
  )
}

function exportReportsCsv(data: ReportsData): void {
  const esc = (s: string): string => `"${s.replace(/"/g, '""')}"`
  const head = ['Reporte', 'Valor', 'Unidad', 'Indicador', 'Cambio']
  const lines = data.reports.map((r) =>
    [r.title, r.value, r.unit ?? '', r.statLabel, r.delta.text].map(esc).join(','),
  )
  const csv = [`# ${data.scope.period}`, head.map(esc).join(','), ...lines].join('\n')
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'reportes.csv'
  a.click()
  URL.revokeObjectURL(url)
}

export function ReportsPage(): JSX.Element {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<ReportPeriod>('30d')
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['reports', period],
    queryFn: () => getReports(period),
  })

  if (isError) {
    return (
      <div data-testid="reports" className="mx-auto w-full max-w-5xl py-10">
        <QueryError message="No pudimos cargar tus reportes." onRetry={() => void refetch()} />
      </div>
    )
  }
  if (isLoading || !data) {
    return (
      <div data-testid="reports" className="mx-auto w-full max-w-5xl py-10">
        <Spinner label="Cargando reportes…" />
      </div>
    )
  }

  const { scope } = data

  return (
    <div data-testid="reports" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title="Reportes"
        subtitle="El desempeño de tu operación, en seis perspectivas."
        actions={
          <Button variant="secondary" size="sm" onClick={() => exportReportsCsv(data)}>
            Exportar todos
          </Button>
        }
      />

      <div className="mt-5 flex flex-col gap-3 rounded-lg border border-line bg-surface p-4 sm:flex-row sm:items-center sm:gap-4">
        <span className="flex-none text-[10px] font-bold uppercase tracking-[0.06em] text-text-3">
          Alcance
        </span>
        <div className="flex flex-wrap gap-2">
          {/* C8: bot + channel are FACTS of the report, not choices — chips
              that mimic the period dropdown promise a control that isn't
              there. Plain labeled text keeps the information honest. */}
          <span className="flex items-center gap-1.5 text-[12px] text-text-2">
            <span className="font-semibold text-text">{scope.bot}</span>
            <span className="text-text-3">· {scope.botHint}</span>
          </span>
          <DropdownMenu
            trigger={
              <button
                type="button"
                aria-label="Elegir periodo"
                className="flex items-center gap-2 rounded-md border border-line bg-field px-3 py-2 transition-colors hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
              >
                <ScopeInner value={scope.period} hint={scope.periodHint} chevron>
                  <Icon>
                    <rect x="3" y="4.5" width="18" height="17" rx="2.4" />
                    <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
                  </Icon>
                </ScopeInner>
              </button>
            }
            items={REPORT_PERIODS.map((p) => ({
              label: p.label,
              onSelect: () => setPeriod(p.value),
            }))}
          />
          <span className="flex items-center gap-1.5 text-[12px] text-text-2">
            <span className="font-semibold text-text">{scope.channel}</span>
            <span className="text-text-3">· {scope.channelHint}</span>
          </span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.reports.map((r) => (
          <ReportCard
            key={r.id}
            r={r}
            onOpen={() => void navigate(`/reports/${r.id}?period=${period}`)}
          />
        ))}
      </div>
    </div>
  )
}
